import React, { useState, useEffect } from 'react'
import { Plan } from '../../../../Entities/Plan';
import { Typography, InputNumber, Button, Divider, List } from 'antd';
import { GlobalOutlined, BgColorsOutlined, FileTextOutlined, TeamOutlined } from '@ant-design/icons';
import './CreditSelector.scss'

interface CreditSelectorProps {
    creditPlan: Plan;
    userCurrencyCode: string;
    userCurrencySymbol: string;
    noOfSelectedCredits: number | null;
    setNoOfSelectedCredits: React.Dispatch<React.SetStateAction<number | null>>;
}

const data = [
    {
        title: 'Custom Domain Linking',
        icon: GlobalOutlined,
    },
    {
        title: 'Custom Branding',
        icon: BgColorsOutlined,
    },
    {
        title: 'Unlimited Job Posts',
        icon: FileTextOutlined,
    },
    {
        title: 'Unlimited Recruiters',
        icon: TeamOutlined,
    },
];

const CreditSelector: React.FC<CreditSelectorProps> = ({
    creditPlan,
    userCurrencyCode,
    userCurrencySymbol,
    noOfSelectedCredits,
    setNoOfSelectedCredits
}) => {
    const [customQuantity, setCustomQuantity] = useState<number | null>(null);

    const selectedVariant = creditPlan.variants.find(
        variant => variant.currency.toLowerCase() === userCurrencyCode.toLowerCase() &&
            variant.billingPeriod.toLowerCase() === "month"
    );

    const inclusivePricePerCredit = selectedVariant?.price || 0;
    const pricePerCredit = Math.round(inclusivePricePerCredit / 1.18); // Assuming the price is already per credit, adjust if needed
    
    const presetQuantities = [10, 100, 1000];

    const handlePresetClick = (quantity: number) => {
        setNoOfSelectedCredits(quantity);
        setCustomQuantity(null);
    };

    const handleCustomQuantityChange = (value: number | null) => {
        setCustomQuantity(value);
        if (value !== null && value >= 1 && value <= 9999) {
            setNoOfSelectedCredits(value);
        }
    };

    useEffect(() => {
        if (noOfSelectedCredits === null || noOfSelectedCredits < 1) {
            setNoOfSelectedCredits(1);
        } else if (noOfSelectedCredits > 99999) {
            setNoOfSelectedCredits(99999);
        }
    }, [noOfSelectedCredits, setNoOfSelectedCredits]);


    return (
        <div className="credit-selector">

            <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                    QualifyMe Credits
                </Typography.Title>
            </div>

            <Divider style={{ margin: 0 }} />

            <Typography.Paragraph type="secondary" style={{ margin: 0, textAlign: "center" }}>
                Prepaid credits for processing job applications — use one credit per application.
            </Typography.Paragraph>

            <div className="credit-selector__benefits">
                <Typography.Text strong>
                    Benefits Included
                </Typography.Text>
                <List
                    itemLayout="horizontal"
                    dataSource={data}
                    size='small'
                    renderItem={(item) => {
                        const IconComponent = item.icon;
                        return (
                            <List.Item>
                                <List.Item.Meta
                                    avatar={<IconComponent className="credit-selector__list-icon" />} title={item.title}
                                />
                            </List.Item>
                        );
                    }}
                />
            </div>


            <Typography.Text strong style={{ margin: 0 }}>
                Pricing
            </Typography.Text>
            <Typography.Title level={4} style={{ margin: 0 }}>
                {userCurrencySymbol}{pricePerCredit.toFixed(2)} / credit
            </Typography.Title>



            <Typography.Text strong>
                Select Quantity
            </Typography.Text>


            <div className="credit-selector__quantity-options-wrapper">
                <div className="credit-selector__preset-buttons-wrapper">
                    {presetQuantities.map((quantity) => (
                        <Button
                            key={quantity}
                            type={noOfSelectedCredits === quantity && customQuantity === null ? "primary" : "default"}
                            onClick={() => handlePresetClick(quantity)}
                            className="credit-selector__preset-btn"
                        >
                            {quantity}
                        </Button>
                    ))}
                </div>
                <div className="credit-selector__custom-input-wrapper">
                    <InputNumber
                        min={1}
                        max={9999}
                        value={customQuantity}
                        onChange={handleCustomQuantityChange}
                        placeholder="Custom"
                        controls={false}
                        addonAfter={<Typography.Text>credits</Typography.Text>}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

        </div>
    )
}

export default CreditSelector